import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge, EmptyState } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Filter, Trash2, Play, CheckCircle2, XCircle } from "lucide-react";

const TYPES = ["validation", "blocking", "calculation", "notification", "approval", "automation"];

export default function Rules() {
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [activeRule, setActiveRule] = useState(null);
  const [testCtx, setTestCtx] = useState('{\n  "amount": 15000\n}');
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({
    name: "", code: "", type: "validation", description: "",
    condition: '{"field":"amount","op":"gt","value":1000}',
    actions: '[{"type":"notify","title":"Alert","body":"Threshold exceeded"}]',
    priority: 100,
  });

  const load = async () => { const r = await api.get("/rules"); setRules(r.data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const condition = JSON.parse(form.condition);
      const actions = JSON.parse(form.actions);
      await api.post("/rules", { ...form, condition, actions, priority: Number(form.priority) });
      toast.success("Rule created"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e) || "Check JSON syntax"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete rule?")) return;
    try { await api.delete(`/rules/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const evaluate = async () => {
    try {
      const context = JSON.parse(testCtx);
      const { data } = await api.post("/rules/evaluate", { rule_id: activeRule.id, context });
      setTestResult(data);
    } catch (e) { toast.error(formatApiError(e) || "Bad context JSON"); }
  };

  return (
    <div data-testid="rules-page">
      <PageHeader title="Rule Engine" subtitle="Configurable rules"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-9 px-4 text-[13px]" data-testid="create-rule-button"><Plus className="w-3.5 h-3.5 mr-1.5"/> New rule</Button></DialogTrigger>
            <DialogContent className="bg-hocp-bgSubtle border-hocp-border max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">New rule</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2"><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="rule-name-input"/></div>
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Priority</Label><Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="rule-priority-input"/></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, "_") })} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="rule-code-input"/></div>
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="rule-type-select"><SelectValue/></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="rule-description-input"/></div>
                <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Condition (JSON)</Label><Textarea value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} rows={5} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono text-[11px]" data-testid="rule-condition-input"/>
                  <div className="text-[10px] mono text-hocp-textTertiary mt-1">Operators: eq, ne, gt, lt, gte, lte, in, nin, contains, exists. Combine with {`{"all":[...]}`} / {`{"any":[...]}`} / {`{"not":{...}}`}.</div>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Actions (JSON array)</Label><Textarea value={form.actions} onChange={(e) => setForm({ ...form, actions: e.target.value })} rows={4} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono text-[11px]" data-testid="rule-actions-input"/></div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-rule-button">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {rules.length === 0 ? <EmptyState icon={Filter} title="No rules configured" subtitle="Create rules to validate, block, calculate, notify, approve, or automate."/> :
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rules.map((r) => (
            <Card key={r.id} className="p-5" data-testid={`rule-card-${r.code}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-[15px] text-white font-semibold">{r.name}</span>
                    <Badge tone="info">{r.type}</Badge>
                    {r.is_active ? <Badge tone="success">ACTIVE</Badge> : <Badge tone="neutral">INACTIVE</Badge>}
                  </div>
                  <div className="mono text-[11px] text-hocp-textMuted mt-0.5">{r.code} · priority {r.priority}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="h-7 w-7 p-0 text-red-400" data-testid={`delete-rule-${r.code}`}><Trash2 className="w-3.5 h-3.5"/></Button>
              </div>
              {r.description && <div className="text-[12px] text-hocp-textMuted mt-2">{r.description}</div>}
              <pre className="mono text-[11px] text-hocp-textMuted mt-3 bg-hocp-bgCard border border-hocp-border rounded px-3 py-2 overflow-x-auto">{JSON.stringify(r.condition, null, 2)}</pre>
              <div className="mt-3">
                <Button size="sm" variant="outline" onClick={() => { setActiveRule(r); setTestResult(null); setTestOpen(true); }} className="h-7 px-3 text-[11px] bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid={`evaluate-rule-${r.code}`}>
                  <Play className="w-3 h-3 mr-1.5"/> Test rule
                </Button>
              </div>
            </Card>
          ))}
        </div>
      }

      {/* Test rule */}
      <Dialog open={testOpen} onOpenChange={(v) => { setTestOpen(v); if (!v) setTestResult(null); }}>
        <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
          <DialogHeader><DialogTitle className="font-heading">Evaluate · <span className="mono text-sm text-hocp-textMuted">{activeRule?.code}</span></DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Context (JSON)</Label><Textarea value={testCtx} onChange={(e) => setTestCtx(e.target.value)} rows={6} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono text-xs" data-testid="rule-test-context-input"/></div>
            {testResult && (
              <div className={`border rounded-md p-4 ${testResult.matched ? "bg-emerald-500/10 border-emerald-500/20" : "bg-red-500/10 border-red-500/20"}`} data-testid="rule-test-result">
                <div className="flex items-center gap-2 mb-2">
                  {testResult.matched ? <CheckCircle2 className="w-4 h-4 text-emerald-400"/> : <XCircle className="w-4 h-4 text-red-400"/>}
                  <span className={`font-heading text-sm ${testResult.matched ? "text-emerald-400" : "text-red-400"}`}>{testResult.matched ? "MATCHED" : "NOT MATCHED"}</span>
                </div>
                {testResult.actions?.length > 0 && (
                  <pre className="mono text-[11px] text-hocp-textMuted mt-2">{JSON.stringify(testResult.actions, null, 2)}</pre>
                )}
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={evaluate} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="run-rule-evaluation">Evaluate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
